import { useState } from 'react';
import {
  Tabs, Table, Button, Group, Stack, Text, Badge, Modal, TextInput, Select, Switch,
  Alert, ActionIcon, Tooltip, Center,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  IconPlus, IconPencil, IconTrash, IconCalendarStats, IconCalendarTime, IconAlertTriangle,
  IconRefresh, IconCircleCheck, IconSchool,
} from '@tabler/icons-react';
import api from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { EmptyState } from '../components/EmptyState';
import { TableSkeleton } from '../components/TableSkeleton';
import { confirmDelete } from '../lib/confirm';
import { formatDate } from '../lib/format';
import { SILENT_ERROR } from '../lib/queryMeta';
import { isoDate, fromIsoDate, apiError, type AcademicTerm } from '../lib/journal';
import { useAcademicYears, type AcademicYearOpt } from '../hooks/useLookups';

/** Chorak tartibi — kontrakt: `index` 1..4. */
const TERM_INDEXES = [1, 2, 3, 4];

/** Xato + "qayta urinish" bloki (etalon: `pages/Contracts.tsx`). */
function LoadError({ message, loading, onRetry }: { message: string; loading: boolean; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <Stack align="center" gap="sm" p="lg">
      <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />} title={t('common.error')} w="100%">
        {message}
      </Alert>
      <Button variant="light" loading={loading} leftSection={<IconRefresh size={16} />} onClick={onRetry}>
        {t('terms.retry')}
      </Button>
    </Stack>
  );
}

// ─────────────────────────────────────────────────────────────
//  O'quv yillari
// ─────────────────────────────────────────────────────────────

function YearsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: years, isLoading, isError, isFetching, refetch } = useAcademicYears();
  const [editing, setEditing] = useState<AcademicYearOpt | null>(null);
  const [opened, setOpened] = useState(false);

  const activeYear = years?.find((y) => y.isActive) ?? null;

  /**
   * Faol yilda sinf bor-yo'qligi. Bu HOLAT REAL: prodda barcha sinflar eski yilda
   * qolib, faol yil bo'sh qolgan edi — foydalanuvchi "jurnal ishlamayapti" deb
   * o'ylardi. Shuning uchun bo'sh holat ochiq va nima qilish kerakligi bilan aytiladi.
   */
  const { data: activeClasses } = useQuery({
    queryKey: ['classes-count-of-year', activeYear?.id],
    queryFn: async () =>
      (await api.get<{ items: unknown[]; total: number }>('/classes', {
        params: { academicYearId: activeYear?.id, page: 1, limit: 1 },
      })).data,
    enabled: !!activeYear?.id,
    meta: SILENT_ERROR,
  });

  const form = useForm({
    initialValues: { name: '', startDate: null as Date | null, endDate: null as Date | null, isActive: false },
    validate: {
      name: (v) => (!v.trim() ? t('terms.nameRequired') : null),
      endDate: (v, values) =>
        v && values.startDate && v <= values.startDate ? t('terms.endAfterStart') : null,
    },
  });

  const openCreate = () => {
    // YANGI YIL AVTOMATIK FAOL QILINMAYDI — bu foydalanuvchi tanlovi.
    // Ilgari (`QuickCreate.tsx`) har yangi yil majburan `isActive: true` bo'lib
    // yaratilar edi va joriy yil bilinmasdan almashib ketardi.
    form.setValues({ name: '', startDate: null, endDate: null, isActive: false });
    form.clearErrors();
    setEditing(null);
    setOpened(true);
  };

  const openEdit = (y: AcademicYearOpt & { startDate?: string | null; endDate?: string | null }) => {
    form.setValues({
      name: y.name,
      startDate: fromIsoDate(y.startDate ? String(y.startDate).slice(0, 10) : null),
      endDate: fromIsoDate(y.endDate ? String(y.endDate).slice(0, 10) : null),
      isActive: !!y.isActive,
    });
    form.clearErrors();
    setEditing(y);
    setOpened(true);
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['academic-years'] });
    qc.invalidateQueries({ queryKey: ['academic-terms'] });
    qc.invalidateQueries({ queryKey: ['academic-term-current'] });
  };

  const saveMut = useMutation({
    mutationFn: (v: typeof form.values) => {
      const body = {
        name: v.name.trim(),
        startDate: isoDate(v.startDate) ?? null,
        endDate: isoDate(v.endDate) ?? null,
        isActive: v.isActive,
      };
      return editing ? api.patch(`/classes/academic-years/${editing.id}`, body) : api.post('/classes/academic-years', body);
    },
    onSuccess: () => {
      notifications.show({ color: 'green', message: editing ? t('terms.yearUpdated') : t('terms.yearCreated') });
      invalidate();
      setOpened(false);
    },
    onError: (e) => notifications.show({ color: 'red', message: apiError(e, t('common.error')) }),
  });

  /** Faol yilni almashtirish — backend qolgan yillarni o'zi o'chiradi (bitta faol yil). */
  const activateMut = useMutation({
    mutationFn: (y: AcademicYearOpt) => api.patch(`/classes/academic-years/${y.id}`, { isActive: true }),
    onSuccess: (_r, y) => {
      notifications.show({ color: 'green', message: t('terms.yearActivated', { name: y.name }) });
      invalidate();
    },
    onError: (e) => notifications.show({ color: 'red', message: apiError(e, t('common.error')) }),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => api.delete(`/classes/academic-years/${id}`),
    onSuccess: () => { notifications.show({ color: 'green', message: t('terms.yearDeleted') }); invalidate(); },
    // 409 — yilga sinflar bog'langan (Prisma P2003). Xom server matni o'rniga
    // nima qilish kerakligini aytadigan xabar.
    onError: (e) => {
      const status = (e as { response?: { status?: number } })?.response?.status;
      notifications.show({ color: 'red', message: status === 409 ? t('terms.yearInUse') : apiError(e, t('common.error')) });
    },
  });

  return (
    <Stack gap="md">
      {!isLoading && !isError && !!years?.length && !activeYear && (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />} title={t('terms.noActiveYear')}>
          {t('terms.noActiveYearDesc')}
        </Alert>
      )}
      {!!activeYear && activeClasses?.total === 0 && (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />} title={t('terms.activeNoClasses', { name: activeYear.name })}>
          <Stack gap="xs" align="flex-start">
            <Text fz="sm" lh={1.5}>{t('terms.activeNoClassesDesc')}</Text>
            <Button size="compact-sm" variant="light" leftSection={<IconSchool size={15} />} onClick={() => navigate('/classes')}>
              {t('terms.goToClasses')}
            </Button>
          </Stack>
        </Alert>
      )}

      <SectionCard
        title={t('terms.yearsTitle')}
        icon={IconCalendarStats}
        p={0}
        right={<Button size="compact-sm" leftSection={<IconPlus size={15} />} onClick={openCreate}>{t('terms.addYear')}</Button>}
      >
        {isLoading ? (
          <TableSkeleton rows={4} cols={4} />
        ) : isError ? (
          <LoadError message={t('terms.yearsLoadError')} loading={isFetching} onRetry={() => void refetch()} />
        ) : !years?.length ? (
          <EmptyState
            icon={IconCalendarStats}
            title={t('terms.yearsEmpty')}
            description={t('terms.yearsEmptyDesc')}
            action={<Button mt="xs" leftSection={<IconPlus size={16} />} onClick={openCreate}>{t('terms.addYear')}</Button>}
          />
        ) : (
          <Table.ScrollContainer minWidth={640}>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('terms.yearName')}</Table.Th>
                  <Table.Th>{t('terms.yearStart')}</Table.Th>
                  <Table.Th>{t('terms.yearEnd')}</Table.Th>
                  <Table.Th>{t('fields.status')}</Table.Th>
                  <Table.Th w={150}>{t('gradesPage.actions')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {years.map((y) => {
                  const row = y as AcademicYearOpt & { startDate?: string | null; endDate?: string | null };
                  return (
                    <Table.Tr key={y.id}>
                      <Table.Td fw={600}>{y.name}</Table.Td>
                      <Table.Td>{row.startDate ? formatDate(row.startDate) : '—'}</Table.Td>
                      <Table.Td>{row.endDate ? formatDate(row.endDate) : '—'}</Table.Td>
                      <Table.Td>
                        {y.isActive ? (
                          <Badge color="teal" variant="light" leftSection={<IconCircleCheck size={13} />}>{t('terms.active')}</Badge>
                        ) : (
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            loading={activateMut.isPending && activateMut.variables?.id === y.id}
                            onClick={() => activateMut.mutate(y)}
                          >
                            {t('terms.makeActive')}
                          </Button>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4} wrap="nowrap">
                          <Tooltip label={t('terms.editYear')}>
                            <ActionIcon variant="subtle" aria-label={t('terms.editYear')} onClick={() => openEdit(row)}>
                              <IconPencil size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label={t('common.delete')}>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              aria-label={t('common.delete')}
                              loading={delMut.isPending && delMut.variables === y.id}
                              onClick={() => confirmDelete(y.name, () => delMut.mutate(y.id))}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>

      <Modal opened={opened} onClose={() => setOpened(false)} title={editing ? t('terms.editYear') : t('terms.addYear')} centered>
        <form onSubmit={form.onSubmit((v) => saveMut.mutate(v))}>
          <Stack>
            <TextInput label={t('terms.yearName')} placeholder="2026-2027" description={t('terms.yearNameHint')} required {...form.getInputProps('name')} />
            <DateInput label={t('terms.yearStart')} valueFormat="DD.MM.YYYY" clearable {...form.getInputProps('startDate')} />
            <DateInput label={t('terms.yearEnd')} valueFormat="DD.MM.YYYY" clearable {...form.getInputProps('endDate')} />
            <Switch
              label={t('terms.makeActive')}
              description={t('terms.activeHint')}
              checked={form.values.isActive}
              // `e.currentTarget` UPDATER ICHIDA O'QILMAYDI — qiymat tashqarida olinadi
              // (React event pooling'da updater kechroq ishlaydi va sahifa oq qolardi).
              onChange={(e) => {
                const checked = e.currentTarget.checked;
                form.setFieldValue('isActive', checked);
              }}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={() => setOpened(false)}>{t('common.cancel')}</Button>
              <Button type="submit" loading={saveMut.isPending}>{t('common.save')}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}

// ─────────────────────────────────────────────────────────────
//  Choraklar (AcademicTerm — kontrakt 2.1)
// ─────────────────────────────────────────────────────────────

function TermsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: years } = useAcademicYears();
  const [yearId, setYearId] = useState<string | null>(null);
  const activeYear = years?.find((y) => y.isActive) ?? null;
  // Tanlanmagan bo'lsa — faol yil (backend ham `academicYearId` siz faol yilni oladi)
  const effectiveYearId = yearId ?? activeYear?.id ?? null;

  const { data: terms, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['academic-terms', effectiveYearId ?? 'active'],
    queryFn: async () =>
      (await api.get<AcademicTerm[]>('/academic-terms', { params: { academicYearId: effectiveYearId || undefined } })).data,
    meta: SILENT_ERROR,
  });

  const [opened, setOpened] = useState(false);
  const [editing, setEditing] = useState<AcademicTerm | null>(null);

  const form = useForm({
    initialValues: { name: '', index: '1', startDate: null as Date | null, endDate: null as Date | null },
    validate: {
      name: (v) => (!v.trim() ? t('terms.nameRequired') : null),
      startDate: (v) => (!v ? t('terms.datesRequired') : null),
      endDate: (v, values) => {
        if (!v) return t('terms.datesRequired');
        if (values.startDate && v <= values.startDate) return t('terms.endAfterStart');
        return null;
      },
    },
  });

  const openCreate = () => {
    const nextIndex = String(Math.min(4, (terms?.length ?? 0) + 1));
    form.setValues({ name: t('gradesPage.termN', { n: nextIndex }), index: nextIndex, startDate: null, endDate: null });
    form.clearErrors();
    setEditing(null);
    setOpened(true);
  };

  const openEdit = (tm: AcademicTerm) => {
    form.setValues({
      name: tm.name,
      index: String(tm.index),
      startDate: fromIsoDate(String(tm.startDate).slice(0, 10)),
      endDate: fromIsoDate(String(tm.endDate).slice(0, 10)),
    });
    form.clearErrors();
    setEditing(tm);
    setOpened(true);
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['academic-terms'] });
    qc.invalidateQueries({ queryKey: ['academic-term-current'] });
  };

  const saveMut = useMutation({
    mutationFn: (v: typeof form.values) => {
      const body = {
        academicYearId: effectiveYearId,
        name: v.name.trim(),
        index: Number(v.index),
        startDate: isoDate(v.startDate),
        endDate: isoDate(v.endDate),
      };
      return editing ? api.patch(`/academic-terms/${editing.id}`, body) : api.post('/academic-terms', body);
    },
    onSuccess: () => {
      notifications.show({ color: 'green', message: editing ? t('terms.termUpdated') : t('terms.termCreated') });
      invalidate();
      setOpened(false);
    },
    // 409 — oraliqlar kesishgan yoki `index` band (kontrakt 2.1 validatsiyasi)
    onError: (e) => {
      const status = (e as { response?: { status?: number } })?.response?.status;
      notifications.show({ color: 'red', message: status === 409 ? apiError(e, t('terms.termOverlap')) : apiError(e, t('common.error')) });
    },
  });

  const delMut = useMutation({
    mutationFn: (id: string) => api.delete(`/academic-terms/${id}`),
    onSuccess: () => { notifications.show({ color: 'green', message: t('terms.termDeleted') }); invalidate(); },
    onError: (e) => {
      const status = (e as { response?: { status?: number } })?.response?.status;
      notifications.show({ color: 'red', message: status === 409 ? t('terms.termInUse') : apiError(e, t('common.error')) });
    },
  });

  const todayIso = isoDate(new Date()) ?? '';

  return (
    <Stack gap="md">
      <Group align="flex-end" gap="sm" wrap="wrap">
        <Select
          label={t('terms.yearColumn')}
          placeholder={t('terms.selectYear')}
          data={(years ?? []).map((y) => ({ value: y.id, label: y.isActive ? `${y.name} · ${t('terms.active')}` : y.name }))}
          value={effectiveYearId}
          onChange={setYearId}
          // Chorak DOIM biror yilga tegishli — bo'sh qiymat mantiqan yo'q.
          allowDeselect={false}
          searchable
          w={240}
        />
      </Group>

      {!years?.length && (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />} title={t('terms.yearsEmpty')}>
          {t('terms.termsNeedYear')}
        </Alert>
      )}

      <SectionCard
        title={t('terms.termsTitle')}
        icon={IconCalendarTime}
        subtitle={t('terms.subtitle')}
        p={0}
        right={
          <Button size="compact-sm" leftSection={<IconPlus size={15} />} disabled={!effectiveYearId} onClick={openCreate}>
            {t('terms.addTerm')}
          </Button>
        }
      >
        {!effectiveYearId ? (
          <Center h={140}><Text c="dimmed">{t('terms.selectYear')}</Text></Center>
        ) : isLoading ? (
          <TableSkeleton rows={4} cols={4} />
        ) : isError ? (
          <LoadError message={t('terms.termsLoadError')} loading={isFetching} onRetry={() => void refetch()} />
        ) : !terms?.length ? (
          <EmptyState
            icon={IconCalendarTime}
            title={t('terms.termsEmpty')}
            description={t('terms.termsEmptyDesc')}
            action={<Button mt="xs" leftSection={<IconPlus size={16} />} onClick={openCreate}>{t('terms.addTerm')}</Button>}
          />
        ) : (
          <Table.ScrollContainer minWidth={680}>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={80}>{t('terms.termIndex')}</Table.Th>
                  <Table.Th>{t('terms.termName')}</Table.Th>
                  <Table.Th>{t('terms.termStart')}</Table.Th>
                  <Table.Th>{t('terms.termEnd')}</Table.Th>
                  <Table.Th w={110}>{t('gradesPage.actions')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {terms.map((tm) => {
                  const s = String(tm.startDate).slice(0, 10);
                  const e = String(tm.endDate).slice(0, 10);
                  const isCurrent = todayIso >= s && todayIso <= e;
                  return (
                    <Table.Tr key={tm.id}>
                      <Table.Td>{tm.index}</Table.Td>
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap">
                          <Text fw={600} fz="sm" lh={1.4}>{tm.name}</Text>
                          {isCurrent && <Badge size="sm" color="teal" variant="light">{t('terms.currentTerm')}</Badge>}
                        </Group>
                      </Table.Td>
                      <Table.Td>{formatDate(s)}</Table.Td>
                      <Table.Td>{formatDate(e)}</Table.Td>
                      <Table.Td>
                        <Group gap={4} wrap="nowrap">
                          <Tooltip label={t('terms.editTerm')}>
                            <ActionIcon variant="subtle" aria-label={t('terms.editTerm')} onClick={() => openEdit(tm)}>
                              <IconPencil size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label={t('common.delete')}>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              aria-label={t('common.delete')}
                              loading={delMut.isPending && delMut.variables === tm.id}
                              onClick={() => confirmDelete(tm.name, () => delMut.mutate(tm.id))}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>

      <Modal opened={opened} onClose={() => setOpened(false)} title={editing ? t('terms.editTerm') : t('terms.addTerm')} centered>
        <form onSubmit={form.onSubmit((v) => saveMut.mutate(v))}>
          <Stack>
            <TextInput label={t('terms.termName')} placeholder="1-chorak" required {...form.getInputProps('name')} />
            <Select
              label={t('terms.termIndex')}
              description={t('terms.termIndexHint')}
              data={TERM_INDEXES.map((n) => ({ value: String(n), label: String(n) }))}
              value={form.values.index}
              onChange={(v) => form.setFieldValue('index', v ?? '1')}
              allowDeselect={false}
            />
            <DateInput label={t('terms.termStart')} valueFormat="DD.MM.YYYY" required {...form.getInputProps('startDate')} />
            <DateInput label={t('terms.termEnd')} valueFormat="DD.MM.YYYY" required {...form.getInputProps('endDate')} />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={() => setOpened(false)}>{t('common.cancel')}</Button>
              <Button type="submit" loading={saveMut.isPending}>{t('common.save')}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}

/**
 * O'quv yili va choraklar — bitta sahifada ikkita tab.
 * Ular ATAYLAB birga: `AcademicTerm` o'quv yiliga bog'lanadi, faol yil esa jurnal,
 * chorak yakuni va baho hisobotining boshlang'ich nuqtasi.
 */
export default function AcademicYears() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<string | null>('years');

  return (
    <>
      <PageHeader title={t('terms.title')} subtitle={t('terms.subtitle')} />
      {/* `keepMounted={false}` — ko'rinmayotgan tab so'rov yubormasin */}
      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="years" leftSection={<IconCalendarStats size={16} />}>{t('terms.tabYears')}</Tabs.Tab>
          <Tabs.Tab value="terms" leftSection={<IconCalendarTime size={16} />}>{t('terms.tabTerms')}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="years"><YearsTab /></Tabs.Panel>
        <Tabs.Panel value="terms"><TermsTab /></Tabs.Panel>
      </Tabs>
    </>
  );
}
